import { Linear, RoughEase, Sine, TimelineMax, TweenMax } from "gsap";
import * as HLF from "html-living-framework";
import { Random } from "html-living-framework";
import * as Constants from "../Data/Constants";
import * as Topics from "../Data/Topics";
import { CharacterName } from "./Character";
import SpotlightFactory from "./SpotlightFactory";

const COLOURS: number[] = [
    0xFFE850, // yellow
    0xFFA3EF, // pink
    0xA0EAFF, // blue
    0x6CDC22, // green
];

const ROTATIONS: number[] = [
    24.57,
    20.42,
    10.61,
    -8.02,
    -16.21,
    -30.80,
];

const MIN_MOVE_DURATION = 1;
const MAX_MOVE_DURATION = 3;

const MAX_ALPHA = 0.3;

const FADE_IN_EASE = RoughEase.ease;
const FADE_IN_DURATION = 1.5;
const FADE_IN_STAGGER = 0.3;

const FADE_OUT_EASE = Linear.easeInOut;
const FADE_OUT_DURATION = 0.7;
const FADE_OUT_STAGGER = 0.1;

const NUM_LIGHTS = 4;

export interface ISpotlightSettings {
    color: number;
    rotation?: number;
    size: "big" | "small";
}

export default class SpotlightParticles extends PIXI.Container {

    protected _spotlights: PIXI.Sprite[];
    protected _messageTokens: any[];
    protected readonly _moveTimeline: TimelineMax = new TimelineMax();
    protected _fadeInTimeline?: TimelineMax;
    protected _fadeOutTimeline?: TimelineMax;

    constructor(
        pSettings: ISpotlightSettings[],
        private readonly MIN_ROTATION: number = 24.57,
        private readonly MAX_ROTATION: number = -30.80,
    ) {
        super();

        this._spotlights = new Array<PIXI.Sprite>(pSettings.length);

        for (let i = 0; i < pSettings.length; i++) {

            const isSmall = pSettings[i].size === "small";

            const spotlight = SpotlightFactory.MakeSpotlight(
                pSettings[i].color,
                pSettings[i].rotation ?? 0, // not currently used, due to the move tween jumping to a start time
                isSmall,
            );

            this._spotlights[i] = spotlight;
            this.addChild(spotlight);
        }

        this._messageTokens = new Array<any>();

        this._messageTokens.push(
            PubSub.subscribe(Topics.GAME_PAUSED, this.onPause.bind(this)),
            PubSub.subscribe(Topics.GAME_UNPAUSED, this.onResume.bind(this)),
        );

        this.MoveLights();

    }

    public destroy(pOptions?: PIXI.DestroyOptions | boolean | undefined) {
        this._messageTokens.forEach(PubSub.unsubscribe);
        this._moveTimeline?.kill();
        this._fadeInTimeline?.kill();
        this._fadeOutTimeline?.kill();
        this._spotlights.forEach(TweenMax.killTweensOf);
        super.destroy(pOptions);
    }
    public FadeIn() {
        HLF.ArrayUtils.shuffle(this._spotlights);
        this._fadeInTimeline?.kill();
        this._fadeOutTimeline?.kill();
        this._fadeInTimeline = new TimelineMax();
        this._fadeInTimeline
            .staggerTo(
                this._spotlights,
                FADE_IN_DURATION,
                {
                    alpha: MAX_ALPHA,
                    ease: FADE_IN_EASE,
                },
                FADE_IN_STAGGER,
            )
            .call(() => this._fadeInTimeline = undefined)
        ;
    }

    public FadeOut() {
        HLF.ArrayUtils.shuffle(this._spotlights);
        this._fadeInTimeline?.kill();
        this._fadeOutTimeline?.kill();
        this._fadeOutTimeline = new TimelineMax();
        this._fadeOutTimeline
            .staggerTo(
                this._spotlights,
                FADE_OUT_DURATION,
                {
                    alpha: 0,
                    ease: FADE_OUT_EASE,
                },
                FADE_OUT_STAGGER,
            )
            .call(() => this._fadeOutTimeline = undefined)
        ;
    }

    /** Should only need to call this once, as they loop forever */
    protected MoveLights() {
        this._moveTimeline.clear();

        const left = this.MAX_ROTATION;
        const right = this.MIN_ROTATION;
        const center = 0;

        for (let i = 0; i < this._spotlights.length; i++) {
            const spotlight = this._spotlights[i];
            const reverse = Random.bool();
            const firstRotation = reverse ? left : right;
            const secondRotation = reverse ? right : left;
            const rotateTimeline = new TimelineMax();

            // duration is in seconds
            const duration = Random.floatBetween(MIN_MOVE_DURATION, MAX_MOVE_DURATION) / this._spotlights.length;

            rotateTimeline
                // using fromTo so that it loops better
                .fromTo(spotlight, duration,
                    {
                        rotation: firstRotation * PIXI.DEG_TO_RAD,
                        ease: Sine.easeOut,
                    },
                    {
                        rotation: center * PIXI.DEG_TO_RAD,
                        height: spotlight.height + 50,
                        ease: Sine.easeIn,
                    },
                )
                .to(spotlight, duration, {
                    rotation: secondRotation * PIXI.DEG_TO_RAD,
                    height: spotlight.height,
                    ease: Sine.easeOut,
                })
                .to(spotlight, duration, {
                    rotation: center * PIXI.DEG_TO_RAD,
                    height: spotlight.height - 250,
                    ease: Sine.easeIn,
                })
                .to(spotlight, duration, {
                    rotation: firstRotation * PIXI.DEG_TO_RAD,
                    height: spotlight.height,
                    ease: Sine.easeOut,
                })
            ;

            rotateTimeline
                .repeat(-1)
                .seek(Math.random() * duration, true ); // random offset on each light

            this._moveTimeline.add(rotateTimeline, 0);

        }

        this._moveTimeline
            .play()
            .eventCallback(Constants.TWEEN_ONCOMPLETE, this.FadeOut.bind(this));

    }

    protected onPause(pTopic: string): void {
        this._moveTimeline.pause();
        this._fadeInTimeline?.pause();
        this._fadeOutTimeline?.pause();
    }

    protected onResume(pTopic: string): void {
        this._moveTimeline.resume();
        this._fadeInTimeline?.resume();
        this._fadeOutTimeline?.resume();
    }
}
